import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

import { type INavigationItem } from '@workinance/shared/ui/AppShellLayoutComponent';
import { BrandWordmarkComponent } from '@workinance/shared/ui/BrandWordmarkComponent';
import { DrawerLayoutComponent } from '@workinance/shared/ui/DrawerLayoutComponent';
import { ChevronDownIcon, MenuIcon } from '@workinance/shared/ui/icons';

/** A group of pages in the side navigation, with an optional small heading such as "Team". */
export interface INavigationSection {
	title?: string;
	items: INavigationItem[];
}

interface ISideNavLayoutComponentProps {
	/** Where the wordmark links to inside the app. */
	homePath: string;
	/** A short tag after the wordmark, such as "Admin". */
	appLabel?: string;
	/** One line under the wordmark saying what the app is for. */
	tagline?: string;
	navigation: INavigationSection[];
	/** Pinned to the bottom of the side navigation, above the account menu, such as a link to another app. */
	sidebarFooter?: ReactNode;
	/** An account menu at the foot of the side navigation, for apps that keep it there rather than in the top bar. */
	accountMenu?: ReactNode;
	/** The middle of the top bar, such as a business switcher. */
	headerCenter?: ReactNode;
	/** The right of the top bar, such as notifications and the account menu. */
	headerEnd?: ReactNode;
	children: ReactNode;
}

interface ISidebarContentComponentProps extends Pick<
	ISideNavLayoutComponentProps,
	| 'homePath'
	| 'appLabel'
	| 'tagline'
	| 'navigation'
	| 'sidebarFooter'
	| 'accountMenu'
> {
	/** Called when a link is followed, so the phone drawer can close. */
	onNavigate?: () => void;
}

function NavigationLinkComponent({
	item: { label, to, icon: Icon },
	onNavigate,
}: {
	item: INavigationItem;
	onNavigate?: () => void;
}) {
	return (
		<NavLink
			to={to ?? ''}
			onClick={onNavigate}
			className={({ isActive }) =>
				parseClassnames(
					'flex items-center gap-3 rounded-[10px] border px-2.5 py-2 text-[15px] text-ink no-underline transition-colors',
					{
						'border-rule bg-panel font-semibold': isActive,
						'border-transparent font-medium hover:bg-panel/70': !isActive,
					},
				)
			}
		>
			{Icon && <Icon className="size-4.5 shrink-0" />} {label}
		</NavLink>
	);
}

function NavigationChildLinkComponent({
	item: { label, to },
	onNavigate,
}: {
	item: INavigationItem;
	onNavigate?: () => void;
}) {
	return (
		<NavLink
			to={to ?? ''}
			onClick={onNavigate}
			className={({ isActive }) =>
				parseClassnames(
					'flex items-center rounded-lg px-2 py-1.5 text-[13.5px] no-underline transition-colors',
					{
						'bg-brand-soft font-semibold text-brand': isActive,
						'font-medium text-ink/70 hover:bg-panel/70 hover:text-ink': !isActive,
					},
				)
			}
		>
			{label}
		</NavLink>
	);
}

/**
 * An item with `children` renders as a collapsible group: a button header toggles its children open
 * and shut, starting open when one of them is the active page.
 */
function NavigationItemComponent({
	item,
	onNavigate,
}: {
	item: INavigationItem;
	onNavigate?: () => void;
}) {
	const { pathname } = useLocation();
	const hasActiveChild = (item.children ?? []).some(
		(child) => child.to && pathname.startsWith(child.to),
	);
	const [isExpanded, setIsExpanded] = useState(hasActiveChild);

	if (item.children && item.children.length > 0) {
		const Icon = item.icon;
		return (
			<div className="flex flex-col gap-px">
				<button
					type="button"
					aria-expanded={isExpanded}
					onClick={() => setIsExpanded((expanded) => !expanded)}
					className="flex w-full cursor-pointer items-center gap-3 rounded-[10px] px-2.5 py-2 text-[15px] font-medium text-ink transition-colors hover:bg-panel/70"
				>
					{Icon && <Icon className="size-4.5 shrink-0" />}
					<span className="flex-1 text-left">{item.label}</span>
					<ChevronDownIcon
						className={parseClassnames(
							'size-4 shrink-0 text-mute transition-transform',
							{ 'rotate-180': isExpanded },
						)}
					/>
				</button>
				{isExpanded && (
					<div className="flex flex-col gap-px pl-6.5">
						{item.children.map((child) => (
							<NavigationChildLinkComponent
								key={child.to}
								item={child}
								onNavigate={onNavigate}
							/>
						))}
					</div>
				)}
			</div>
		);
	}

	return <NavigationLinkComponent item={item} onNavigate={onNavigate} />;
}

function SidebarContentComponent({
	homePath,
	appLabel,
	tagline,
	navigation,
	sidebarFooter,
	accountMenu,
	onNavigate,
}: ISidebarContentComponentProps) {
	return (
		<div className="flex h-full flex-col">
			<Link
				to={homePath}
				onClick={onNavigate}
				aria-label="Workinance home"
				className="flex items-baseline gap-2 px-2 pt-1 no-underline"
			>
				<BrandWordmarkComponent className="text-xl" />
				{appLabel && (
					<span className="font-code text-[11px] tracking-[0.06em] text-mute uppercase">
						{appLabel}
					</span>
				)}
			</Link>
			{tagline && <p className="mt-1.5 px-2 text-xs text-mute">{tagline}</p>}

			<nav aria-label="Main" className="mt-6 flex flex-col gap-1">
				{navigation.map((section, sectionIndex) => (
					<div
						key={section.title ?? sectionIndex}
						className="flex flex-col gap-1"
					>
						{section.title && (
							<p className="mt-4 mb-1 px-2.5 font-code text-[11px] tracking-[0.06em] text-mute uppercase">
								{section.title}
							</p>
						)}
						{section.items.map((item) => (
							<NavigationItemComponent
								key={item.to ?? item.label}
								item={item}
								onNavigate={onNavigate}
							/>
						))}
					</div>
				))}
			</nav>

			<div className="mt-auto flex flex-col gap-2.5 pt-6">
				{sidebarFooter}
				{accountMenu}
			</div>
		</div>
	);
}

/**
 * The frame around every signed-in page in the dashboard app: a slim side navigation on the left and a
 * top bar. Pages carry their own titles, so the top bar does not repeat them. On phones and tablets the
 * side navigation becomes a drawer: the top bar then shows the wordmark on the left, the middle slot, and
 * the menu button on the right.
 */
export function SideNavLayoutComponent({
	homePath,
	appLabel,
	tagline,
	navigation,
	sidebarFooter,
	accountMenu,
	headerCenter,
	headerEnd,
	children,
}: ISideNavLayoutComponentProps) {
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const sidebarProps = {
		homePath,
		appLabel,
		tagline,
		navigation,
		sidebarFooter,
		accountMenu,
	};

	return (
		<div className="min-h-dvh bg-paper font-grotesk text-ink lg:flex">
			<aside className="sticky top-0 hidden h-dvh w-47 shrink-0 overflow-y-auto border-r border-rule px-2.5 py-4.5 lg:block">
				<SidebarContentComponent {...sidebarProps} />
			</aside>

			<div className="flex min-w-0 flex-1 flex-col">
				<header className="sticky top-0 z-30 flex min-h-16 items-center gap-3 border-b border-rule bg-paper/85 px-[clamp(16px,3vw,28px)] py-3 backdrop-blur lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
					{/* The side navigation (and its wordmark) is hidden on phones and tablets, so a short mark stands in for it here. */}
					<div className="shrink-0 lg:min-w-0">
						<Link
							to={homePath}
							aria-label="Workinance home"
							className="no-underline lg:hidden"
						>
							<BrandWordmarkComponent isShortMark />
						</Link>
					</div>
					<div className="flex min-w-0 flex-1 justify-center lg:justify-self-center">
						{headerCenter}
					</div>
					<div className="flex shrink-0 items-center gap-2 justify-self-end">
						{headerEnd}
						<button
							type="button"
							aria-label="Open menu"
							onClick={() => setIsDrawerOpen(true)}
							className="-mr-2 grid size-10 shrink-0 cursor-pointer place-items-center rounded-xl transition-colors hover:bg-brand-soft lg:hidden"
						>
							<MenuIcon className="size-5" />
						</button>
					</div>
				</header>

				<main className="w-full max-w-350 px-[clamp(16px,3vw,28px)] pt-6 pb-16">
					{children}
				</main>
			</div>

			<DrawerLayoutComponent
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				title="Menu"
				size="sm"
			>
				<SidebarContentComponent
					{...sidebarProps}
					onNavigate={() => setIsDrawerOpen(false)}
				/>
			</DrawerLayoutComponent>
		</div>
	);
}
